import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Package, Plus, X, Loader2, TrendingUp, Play, Square, Repeat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Ürün Ayarları alt panelleri. `view` ile hangi panelin gösterileceği seçilir:
//   "units"  → Ölçü Birimleri
//   "market" → Katalog Araçları · Piyasa Fiyatı Araştırması (otomatik-devam)
// Her panel Ürün Ayarları menüsünde AYRI bir öğe/dialog olarak açılır (iç içe DEĞİL).
export default function ProductSettings({ hideHeader = false, view = "units" }) {
  const { can } = useAuth();
  const editable = can("config.manage");
  const canMarket = can("price.write");

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newUnit, setNewUnit] = useState("");
  const [mp, setMp] = useState(null);
  const [mpMethod, setMpMethod] = useState("scraper");
  const [mpScope, setMpScope] = useState("missing");
  const [mpLimit, setMpLimit] = useState(300);
  const [mpAuto, setMpAuto] = useState(true);
  const [mpBusy, setMpBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/settings/product-units"); setUnits(r.data.units || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (view === "units") load(); }, [view]);

  const loadMp = async () => {
    try { const { data } = await api.get("/products/market-price/status"); setMp(data); }
    catch { /* yetki yoksa sessiz geç */ }
  };
  useEffect(() => { if (view === "market" && canMarket) loadMp(); }, [view, canMarket]);
  // Piyasa paneli açıkken canlı durumu 4sn'de bir yenile (otomatik-devam gruplar arası
  // kısa boşlukta bile ilerleme görünür kalsın).
  useEffect(() => {
    if (view !== "market" || !canMarket) return;
    const t = setInterval(loadMp, 4000);
    return () => clearInterval(t);
  }, [view, canMarket]);

  const runMp = async () => {
    setMpBusy(true);
    try {
      const { data } = await api.post("/products/market-price/run", { method: mpMethod, scope: mpScope, limit: Number(mpLimit) || undefined, auto_continue: mpAuto && mpScope === "missing" });
      if (data.started > 0) { toast.success(data.message); setMp((s) => ({ ...(s || {}), running: true, done: 0, total: data.started, ok: 0, failed: 0, auto_continue: data.auto_continue })); loadMp(); }
      else toast.info(data.message || "Araştırılacak ürün yok");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setMpBusy(false); }
  };
  const cancelMp = async () => {
    try { await api.post("/products/market-price/cancel"); toast.info("İptal istendi"); loadMp(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const persist = async (next) => {
    setSaving(true);
    try { const r = await api.put("/settings/product-units", { units: next }); setUnits(r.data.units || next); toast.success("Kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
    finally { setSaving(false); }
  };
  const add = () => {
    const u = newUnit.trim();
    if (!u) return;
    if (units.some((x) => x.toLowerCase() === u.toLowerCase())) { toast.error("Bu birim zaten var"); return; }
    setNewUnit("");
    const next = [...units, u];
    setUnits(next); persist(next);
  };
  const remove = (u) => {
    if (units.length <= 1) { toast.error("En az bir birim kalmalı"); return; }
    const next = units.filter((x) => x !== u);
    setUnits(next); persist(next);
  };

  // -------- Ölçü Birimleri paneli --------
  if (view === "units") {
    return (
      <div data-testid="product-settings-units" className="max-w-2xl space-y-5">
        {!hideHeader && (
          <div>
            <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Package className="h-5 w-5 text-brand-green" /> Ölçü Birimleri</p>
            <p className="mt-1 text-sm text-slate-500">Belge oluştururken kalem ölçü birimleri bu listeden seçilir.</p>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="font-heading text-sm font-bold text-slate-800">Ölçü Birimleri {saving && <Loader2 className="ml-1 inline h-3.5 w-3.5 animate-spin text-slate-400" />}</p>
            <span className="text-[11px] text-slate-400">{units.length} birim</span>
          </div>
          <p className="mb-3 mt-0.5 text-[11px] text-slate-400">Değişiklikler <b>anında</b> kaydedilir.</p>
          {loading ? (
            <p className="text-sm text-slate-400" data-testid="units-loading"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Yükleniyor…</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2" data-testid="unit-chips">
                {units.length === 0 && <span className="text-sm text-slate-400">Henüz birim yok.</span>}
                {units.map((u) => (
                  <span key={u} data-testid={`unit-chip-${u}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                    {u}
                    {editable && (
                      <button type="button" data-testid={`unit-del-${u}`} onClick={() => remove(u)} title="Sil"
                        className="rounded-full p-0.5 text-emerald-500 transition-colors hover:bg-red-50 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                    )}
                  </span>
                ))}
              </div>
              {editable && (
                <div className="mt-4 flex gap-2">
                  <Input data-testid="unit-new-input" value={newUnit} onChange={(e) => setNewUnit(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder="Yeni birim (örn: Ton, Deste, Rulo)" className="h-10 max-w-xs" />
                  <Button data-testid="unit-add-btn" onClick={add} disabled={saving} className="gap-1.5"><Plus className="h-4 w-4" /> Ekle</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // -------- Katalog Araçları · Piyasa Fiyatı Araştırması paneli --------
  if (!canMarket) {
    return <p data-testid="market-price-no-perm" className="text-sm text-slate-400">Piyasa fiyatı araştırması için yetkiniz yok.</p>;
  }
  return (
    <div data-testid="product-settings-market" className="max-w-2xl space-y-5">
      {!hideHeader && (
        <div>
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><TrendingUp className="h-5 w-5 text-brand-green" /> Piyasa Fiyatı Araştırması</p>
          <p className="mt-1 text-sm text-slate-500">Ürünlerin güncel piyasa fiyatını araştırır.</p>
        </div>
      )}
      <div data-testid="market-price-settings" className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 font-heading text-sm font-bold text-slate-800"><TrendingUp className="h-4 w-4 text-brand-green" /> Piyasa Fiyatı Araştırması</p>
          <span className="text-[11px] text-slate-400" data-testid="mp-missing-count">{mp?.missing_total ?? "—"} ürün fiyatsız</span>
        </div>
        <p className="mb-3 mt-0.5 text-[11px] text-slate-400">Ürünlerin güncel piyasa fiyatını araştırıp <b>piyasa fiyatı</b> alanına yazar. Site tarama ücretsizdir; Google AI daha güvenilirdir (LLM kullanır). Aktif kullanıcı yokken haftalık otomatik de çalışır.</p>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="text-xs text-slate-600">Yöntem
            <select data-testid="mp-method" value={mpMethod} onChange={(e) => setMpMethod(e.target.value)} disabled={mp?.running}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-2 text-sm">
              <option value="scraper">Site Tarama (ücretsiz)</option>
              <option value="ai">Google AI (güvenilir)</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">Kapsam
            <select data-testid="mp-scope" value={mpScope} onChange={(e) => setMpScope(e.target.value)} disabled={mp?.running}
              className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-2 text-sm">
              <option value="missing">Fiyatı olmayanlar</option>
              <option value="all">Tüm ürünler (yeniden)</option>
            </select>
          </label>
          <label className="text-xs text-slate-600">Üst sınır (grup)
            <Input data-testid="mp-limit" type="number" min="1" max="5000" value={mpLimit} onChange={(e) => setMpLimit(e.target.value)} disabled={mp?.running} className="mt-1 h-9" />
          </label>
        </div>

        {/* Otomatik bul (auto-continue) — AI görsel üretimindeki gibi: işaretliyse bitince kalanları buldukça devam eder */}
        <label data-testid="mp-auto" className={`mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors ${mpScope !== "missing" ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60" : mpAuto ? "border-brand-green/40 bg-emerald-50/60" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <input type="checkbox" data-testid="mp-auto-checkbox" checked={mpAuto && mpScope === "missing"} disabled={mp?.running || mpScope !== "missing"}
            onChange={(e) => setMpAuto(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Repeat className="h-3.5 w-3.5 text-brand-green" /> Otomatik bul (bitince devam et)</span>
            <span className="mt-0.5 block text-[11px] text-slate-500">İşaretliyse, bir grup bitince fiyatsız ürün kalmayana kadar sıradaki grubu otomatik başlatır. {mpScope !== "missing" && "(Yalnız \"Fiyatı olmayanlar\" kapsamında geçerli.)"}</span>
          </span>
        </label>

        {mp?.running ? (
          <div className="mt-3" data-testid="mp-progress">
            <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
              <span>Araştırılıyor… {mp.done}/{mp.total} · <b className="text-emerald-600">{mp.ok} bulundu</b>{mp.failed ? ` · ${mp.failed} boş` : ""}{mp.auto_continue ? " · otomatik devam" : ""}</span>
              <span>{mp.total ? Math.round((mp.done / mp.total) * 100) : 0}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-brand-green transition-all" style={{ width: `${mp.total ? (mp.done / mp.total) * 100 : 0}%` }} />
            </div>
            {mp.auto_continue && <p className="mt-1 text-[10px] text-emerald-600" data-testid="mp-auto-badge">Otomatik devam açık — kalan {mp.missing_total ?? 0} fiyatsız ürün için gruplar sırayla çalışacak.</p>}
            <Button data-testid="mp-cancel" onClick={cancelMp} size="sm" variant="outline" className="mt-2 border-red-200 text-red-600 hover:bg-red-50"><Square className="mr-1.5 h-3.5 w-3.5" /> Durdur</Button>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button data-testid="mp-run" onClick={runMp} disabled={mpBusy} className="bg-brand-green text-white hover:bg-brand-green-600">
              {mpBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />} Piyasa Fiyatlarını Araştır
            </Button>
            {mp?.finished_at && <span className="text-[11px] text-slate-400" data-testid="mp-last-run">Son: {String(mp.finished_at).slice(0, 16).replace("T", " ")} · {mp.ok}/{mp.total} bulundu{mp.stop_reason ? ` · ${mp.stop_reason}` : ""}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
